import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  FilePdf, Plus, Trash, FloppyDisk, UploadSimple, Eye, EyeSlash,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ASSET_BASE = process.env.REACT_APP_BACKEND_URL || "";
const resolveUrl = (u) => !u ? "" : (u.startsWith("http") ? u : `${ASSET_BASE}/${u.replace(/^\/+/, "")}`);

function PaperRow({ paper, onUpdate, onDelete, onUploadPdf, isUploading }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    title: paper.title, authors: paper.authors, abstract: paper.abstract,
    external_url: paper.external_url, type: paper.type, published_at: paper.published_at,
    is_published: paper.is_published,
  });

  return (
    <div className="bg-stone-900 rounded-xl border border-stone-800 p-4 space-y-2"
         data-testid={`admin-paper-${paper.id}`}>
      {editing ? (
        <>
          <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            placeholder="Title"
            className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500" />
          <input value={draft.authors} onChange={(e) => setDraft({ ...draft, authors: e.target.value })}
            placeholder="Authors (comma-separated)"
            className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500" />
          <textarea value={draft.abstract} onChange={(e) => setDraft({ ...draft, abstract: e.target.value })}
            placeholder="Abstract" rows={3}
            className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-stone-100 focus:outline-none focus:border-emerald-500" />
          <input value={draft.external_url} onChange={(e) => setDraft({ ...draft, external_url: e.target.value })}
            placeholder="External URL (optional, e.g. DOI or arXiv)"
            className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white font-mono focus:outline-none focus:border-emerald-500" />
          <div className="flex items-center gap-3">
            <select value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value })}
              className="bg-stone-800 border border-stone-700 rounded-lg px-2 py-1.5 text-xs text-white">
              <option value="research">Research paper</option>
              <option value="whitepaper">Whitepaper</option>
            </select>
            <input type="date" value={draft.published_at?.slice(0, 10) || ""}
              onChange={(e) => setDraft({ ...draft, published_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
              className="bg-stone-800 border border-stone-700 rounded-lg px-2 py-1.5 text-xs text-white" />
            <label className="text-[10px] text-stone-500 uppercase tracking-wider flex items-center gap-1.5">
              <input type="checkbox" checked={draft.is_published}
                onChange={(e) => setDraft({ ...draft, is_published: e.target.checked })} />
              Published
            </label>
            <div className="flex-1" />
            <button onClick={() => { onUpdate(paper.id, draft); setEditing(false); }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-3 py-1.5 rounded-lg inline-flex items-center gap-1"
              data-testid={`save-paper-${paper.id}`}>
              <FloppyDisk size={12} /> Save
            </button>
            <button onClick={() => setEditing(false)}
              className="bg-stone-800 hover:bg-stone-700 text-stone-300 text-xs px-3 py-1.5 rounded-lg">
              Cancel
            </button>
          </div>
        </>
      ) : (
        <div className="flex items-start gap-3">
          <FilePdf size={18} weight="duotone" className="text-emerald-400 shrink-0 mt-1" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-600/20 text-emerald-400 font-bold uppercase tracking-wider">
                {paper.type}
              </span>
              {!paper.is_published && (
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-stone-700 text-stone-400 font-bold uppercase tracking-wider">
                  Hidden
                </span>
              )}
              <p className="font-heading text-sm font-semibold text-white truncate">{paper.title}</p>
            </div>
            {paper.authors && <p className="text-xs text-stone-500 mt-0.5">{paper.authors}</p>}
            {paper.abstract && <p className="text-xs text-stone-400 mt-1 line-clamp-2">{paper.abstract}</p>}
            <div className="flex gap-3 text-[11px] text-stone-500 mt-2">
              {paper.pdf_url && <a href={resolveUrl(paper.pdf_url)} target="_blank" rel="noreferrer" className="text-emerald-400 hover:text-emerald-300">Preview PDF</a>}
              {paper.published_at && <span>{new Date(paper.published_at).toLocaleDateString()}</span>}
            </div>
          </div>
          <div className="flex flex-col gap-1 shrink-0">
            <button onClick={() => setEditing(true)}
              className="bg-stone-800 hover:bg-stone-700 text-stone-300 text-xs px-3 py-1.5 rounded-lg"
              data-testid={`edit-paper-${paper.id}`}>Edit</button>
            <label className={`text-xs px-3 py-1.5 rounded-lg inline-flex items-center gap-1 ${isUploading ? "bg-emerald-600/20 text-emerald-300 cursor-wait" : "bg-stone-800 hover:bg-stone-700 text-stone-300 cursor-pointer"}`}
                   data-testid={`upload-paper-${paper.id}`}>
              {isUploading ? (
                <>
                  <svg className="animate-spin" width="11" height="11" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeDasharray="60" strokeLinecap="round" opacity="0.4"/>
                    <path d="M22 12a10 10 0 01-10 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round"/>
                  </svg>
                  Uploading…
                </>
              ) : (
                <>
                  <UploadSimple size={11} /> PDF
                </>
              )}
              <input type="file" accept="application/pdf,.pdf" className="hidden" disabled={isUploading}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = ""; // allow re-uploading the same file
                  if (f) onUploadPdf(paper.id, f);
                }} />
            </label>
            <button onClick={() => onUpdate(paper.id, { is_published: !paper.is_published })}
              className={`text-xs px-3 py-1.5 rounded-lg inline-flex items-center gap-1 ${paper.is_published ? "bg-amber-600/20 text-amber-300" : "bg-emerald-600/20 text-emerald-300"}`}
              data-testid={`toggle-paper-${paper.id}`}>
              {paper.is_published ? <><EyeSlash size={11} /> Hide</> : <><Eye size={11} /> Show</>}
            </button>
            <button onClick={() => window.confirm(`Delete "${paper.title}"?`) && onDelete(paper.id)}
              className="bg-red-600/20 hover:bg-red-600/30 text-red-300 text-xs px-3 py-1.5 rounded-lg inline-flex items-center gap-1"
              data-testid={`delete-paper-${paper.id}`}>
              <Trash size={11} /> Del
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ResearchPapersManager() {
  const [papers, setPapers] = useState([]);
  const [msg, setMsg] = useState("");
  const [adding, setAdding] = useState(false);
  const [newP, setNewP] = useState({ title: "", authors: "", abstract: "", type: "research", external_url: "", is_published: true });

  const fetchAll = useCallback(async () => {
    const r = await axios.get(`${API}/admin/research-papers`, { withCredentials: true });
    setPapers(r.data?.papers || []);
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const create = async () => {
    if (!newP.title.trim()) return setMsg("Title required");
    try {
      await axios.post(`${API}/admin/research-papers`, newP, { withCredentials: true });
      setNewP({ title: "", authors: "", abstract: "", type: "research", external_url: "", is_published: true });
      setAdding(false);
      setMsg("Paper created — upload the PDF next.");
      fetchAll();
    } catch (e) { setMsg(e.response?.data?.detail || "Create failed"); }
  };

  const update = async (id, patch) => {
    await axios.put(`${API}/admin/research-papers/${id}`, patch, { withCredentials: true });
    fetchAll();
  };
  const remove = async (id) => {
    await axios.delete(`${API}/admin/research-papers/${id}`, { withCredentials: true });
    fetchAll();
  };
  const [uploadingId, setUploadingId] = useState(null);

  const uploadPdf = async (id, file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      setMsg(`❌ PDF is ${(file.size / 1024 / 1024).toFixed(1)} MB — please keep under 25 MB.`);
      return;
    }
    if (!/pdf$/i.test(file.type) && !/\.pdf$/i.test(file.name)) {
      setMsg("❌ Only PDF files are allowed.");
      return;
    }
    setUploadingId(id);
    setMsg(`Uploading ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)…`);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/admin/research-papers/${id}/pdf`, fd, {
        withCredentials: true,
        // NB: DO NOT set Content-Type manually — the browser must add the
        // multipart boundary or FastAPI cannot parse the body.
      });
      setMsg(`✅ PDF uploaded for “${papers.find((p) => p.id === id)?.title || "paper"}”.`);
      await fetchAll();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Upload failed";
      setMsg(`❌ Upload failed: ${detail}`);
    } finally {
      setUploadingId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="research-papers-manager">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-emerald-600/20 flex items-center justify-center shrink-0">
          <FilePdf size={18} weight="duotone" className="text-emerald-400" />
        </div>
        <div className="flex-1">
          <h2 className="font-heading text-lg font-semibold text-white">Research Papers & Whitepapers</h2>
          <p className="text-xs text-stone-400 mt-0.5">
            Publish research papers and whitepapers. Whitepapers appear as cards on the home page; research papers list on the /research-papers page.
          </p>
        </div>
        <button onClick={() => setAdding(!adding)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-3 py-2 rounded-lg inline-flex items-center gap-1.5"
          data-testid="add-paper-btn">
          <Plus size={14} weight="bold" /> {adding ? "Cancel" : "Add Paper"}
        </button>
      </div>

      {msg && (
        <div className="bg-emerald-600/10 border border-emerald-600/30 rounded-lg px-3 py-2 text-xs text-emerald-300"
             data-testid="papers-msg">{msg}</div>
      )}

      {adding && (
        <div className="bg-stone-900 rounded-xl border border-emerald-700/30 p-4 space-y-2">
          <input value={newP.title} onChange={(e) => setNewP({ ...newP, title: e.target.value })}
            placeholder="Title" data-testid="new-paper-title"
            className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500" />
          <input value={newP.authors} onChange={(e) => setNewP({ ...newP, authors: e.target.value })}
            placeholder="Authors" data-testid="new-paper-authors"
            className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500" />
          <textarea value={newP.abstract} onChange={(e) => setNewP({ ...newP, abstract: e.target.value })}
            placeholder="Abstract" rows={3} data-testid="new-paper-abstract"
            className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-stone-100 focus:outline-none focus:border-emerald-500" />
          <input value={newP.external_url} onChange={(e) => setNewP({ ...newP, external_url: e.target.value })}
            placeholder="External URL (optional)"
            className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white font-mono focus:outline-none focus:border-emerald-500" />
          <div className="flex items-center gap-3">
            <select value={newP.type} onChange={(e) => setNewP({ ...newP, type: e.target.value })}
              className="bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white"
              data-testid="new-paper-type">
              <option value="research">Research paper</option>
              <option value="whitepaper">Whitepaper</option>
            </select>
            <label className="text-[10px] text-stone-500 uppercase tracking-wider flex items-center gap-1.5">
              <input type="checkbox" checked={newP.is_published}
                onChange={(e) => setNewP({ ...newP, is_published: e.target.checked })} />
              Publish immediately
            </label>
            <div className="flex-1" />
            <button onClick={create}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-4 py-2 rounded-lg inline-flex items-center gap-1"
              data-testid="create-paper-submit">
              <FloppyDisk size={12} /> Create
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {papers.length === 0 && (
          <p className="text-stone-500 text-sm text-center py-6">No papers yet — click Add Paper.</p>
        )}
        {papers.map((p) => (
          <PaperRow key={p.id} paper={p} onUpdate={update} onDelete={remove} onUploadPdf={uploadPdf} isUploading={uploadingId === p.id} />
        ))}
      </div>
    </div>
  );
}
